import { Tag } from 'lucide-react';
import { agentLabelRefName, type AgentAssociationRef } from '@reflex/shared';
import { useAgentListFilterActions } from '@/components/agent/agent-list-filter-context';
import { TaxonomyChip } from '@/components/agent/TaxonomyChip';
import { useRemoveAgentLabel } from '@/hooks/agents/useAgentLabels';

export interface AgentLabelChipProps {
  /** The membership: `externalId` is the label id, `data.name` its display name. */
  association: AgentAssociationRef;
  /** The agent wearing the label. Without it, removal is not offered. */
  agentId?: string;
  /** Collapse to the icon when the strip runs out of room. */
  iconOnly?: boolean;
}

/**
 * The chip for a label — the kind of work a run is. Shares its shell with the
 * group chip through {@link TaxonomyChip}, so the two taxonomies behave
 * identically even though one is applied by hand and the other by the
 * classifier.
 *
 * The difference is what removal means. Taking a label off is a correction of
 * the classifier, not a filing decision, which is why it stays one click away
 * on the chip itself rather than living in a menu.
 */
export function AgentLabelChip({ association, agentId, iconOnly }: AgentLabelChipProps) {
  const labelId = association.externalId;
  const filterActions = useAgentListFilterActions();
  const removeLabel = useRemoveAgentLabel();
  const isActiveFilter = filterActions?.activeLabelId === labelId;

  return (
    <TaxonomyChip
      noun="Label"
      name={agentLabelRefName(association)}
      icon={Tag}
      iconOnly={iconOnly}
      isActiveFilter={isActiveFilter}
      onToggleFilter={
        filterActions
          ? () => filterActions.setLabelFilter(isActiveFilter ? null : labelId)
          : undefined
      }
      onRemove={agentId ? () => removeLabel.mutate({ labelId, agentIds: [agentId] }) : undefined}
      removeActionLabel="Remove label"
      removePending={removeLabel.isPending}
    />
  );
}
